import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
// v0.6.2: import the shared rowBackground helper from
// the live feed panel so the admin preview renders the
// same gradient the wall TV will. Keeping the math in
// one place (LiveFeedPanel.tsx) and importing it here
// means a future tweak to the gradient formula is a
// single-file change; the preview always matches the
// production render.
import { rowBackground } from '../public-board/LiveFeedPanel';
import { ChronicleThemeEditor } from './chronicles/ChronicleThemeEditor';

interface BoardConfig {
  panels: string[];
  rotation_seconds: number;
  anonymise: boolean;
  sound: boolean;
  // M5.11: per-team timeout for the live_feed panel.
  // Events older than this many minutes are excluded
  // from /api/public-board/live-feed. Admin-set; the
  // api clamps to [1, 10080] (1 minute..1 week) and
  // falls back to 60 when the value is missing or
  // invalid. The PATCH response echoes the resolved
  // value so the admin sees what the server stored.
  live_feed_timeout_minutes?: number;
  // v0.6.2: per-team display color for the live feed
  // row backgrounds. RGB triplet [r, g, b] each in
  // [0, 255]. The api returns the array shape; the
  // admin's native <input type="color"> sends the
  // '#rrggbb' hex string (the api coerces both). The
  // wall TV uses this as the base hue and computes a
  // per-row gradient: newest rows get a light tint,
  // oldest get a deep, saturated version. Admin
  // picks a different color per team if they want
  // (e.g. blue for engineering, purple for design).
  live_feed_item_color?: [number, number, number];
}
interface Team { id: string; name: string; }

const ALL_PANELS = [
  { id: 'live_feed', label: '⚡ Live Feed', desc: 'Most recent events with XP/coin gains, fades in as they happen' },
  { id: 'on_fire', label: '🔥 On Fire', desc: 'Top streak leaders' },
  { id: 'just_shipped', label: '🚀 Just Shipped', desc: 'Recent merges + quest completions' },
  { id: 'kudos_stream', label: '💛 Kudos Stream', desc: 'Recent peer kudos' },
  { id: 'quest_of_the_day', label: '🗺️ Quest of the Day', desc: 'Featured open quest' },
  { id: 'class_spotlight', label: '⚔️ Class Spotlight', desc: 'Class distribution' },
  { id: 'todays_standouts', label: '⭐ Recent Standouts', desc: 'Top XP earners in the last 24h (rolling window)' },
  { id: 'competency_heatmap', label: '🧊 Competency Heatmap', desc: 'Team avg per competency' },
];

// Admin display settings for the public board: which panels are enabled
// (+ their order), rotation interval, anonymisation override, sound.
export function PublicBoardSettingsPage() {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });
  // The team picker is driven by ?team_id= in the URL so the
  // selection is bookmarkable / shareable. Falls back to the first
  // team alphabetically when no param is set.
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';
  const setTeamId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team_id', id); else next.delete('team_id');
    setSearchParams(next, { replace: true });
  };
  const board = useQuery({
    queryKey: ['public-board-config', teamId],
    queryFn: () => api<{ config: BoardConfig }>(`/api/public-board?team_id=${teamId}`),
    enabled: !!teamId,
  });
  const cfg = board.data?.config;
  const [enabled, setEnabled] = useState<string[]>(cfg?.panels ?? []);
  const [rotation, setRotation] = useState(cfg?.rotation_seconds ?? 5);
  const [anonymise, setAnonymise] = useState(cfg?.anonymise ?? true);
  const [sound, setSound] = useState(cfg?.sound ?? false);
  // M5.11: local state for the live_feed timeout.
  // Default 60 minutes (the server's default; the api
  // also falls back to 60 when the value is missing
  // or invalid, so the two are aligned). Admin sets
  // it in a number input below; the api clamps to
  // [1, 10080] on PATCH so a typo can't persist a
  // pathological value to the DB.
  const [liveFeedTimeoutMin, setLiveFeedTimeoutMin] = useState<number>(cfg?.live_feed_timeout_minutes ?? 60);
  // v0.6.2: local state for the live feed row color.
  // The api stores the [r, g, b] array shape; the
  // native <input type="color"> reads/writes
  // '#rrggbb' hex strings. The bridge function
  // rgbToHex keeps the two representations in sync.
  // Default is a muted green that reads on the wall
  // TV's dark background; the api's
  // DEFAULT_LIVE_FEED_ITEM_COLOR constant.
  const defaultItemColor: [number, number, number] = [34, 197, 94];
  const [itemColor, setItemColor] = useState<[number, number, number]>(cfg?.live_feed_item_color ?? defaultItemColor);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Sync local state when the query loads (or when the team
  // changes — reset to the new team's config). Only runs when
  // board.data changes reference, so we don't fight the user's
  // local edits mid-save.
  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.panels);
    setRotation(cfg.rotation_seconds);
    setAnonymise(cfg.anonymise);
    setSound(cfg.sound);
    setLiveFeedTimeoutMin(cfg.live_feed_timeout_minutes ?? 60);
    setItemColor(cfg.live_feed_item_color ?? defaultItemColor);
  }, [teamId, board.data?.config?.panels?.length, board.data?.config?.rotation_seconds, board.data?.config?.anonymise, board.data?.config?.sound]);

  const save = useMutation({
    mutationFn: () => api<{ config: BoardConfig }>(`/api/admin/teams/${teamId}/public-board-config`, {
      method: 'PATCH', body: {
        panels: enabled,
        rotation_seconds: rotation,
        anonymise,
        sound,
        // M5.11: send the live_feed_timeout_minutes
        // value to the server. The api clamps to
        // [1, 10080] and echoes the resolved value
        // back; we use the response to update the local
        // state in onSuccess so the input shows what
        // the server actually stored (catches typo
        // clamp behaviour).
        live_feed_timeout_minutes: liveFeedTimeoutMin,
        // v0.6.2: send the live_feed_item_color as
        // the [r, g, b] array. The api also accepts
        // '#rrggbb' hex; the array is the canonical
        // stored shape and the read path doesn't have
        // to re-parse on every wall-TV poll.
        live_feed_item_color: itemColor,
      },
    }),
    onSuccess: (data) => {
      setOk('Saved.');
      setErr(null);
      // Sync the local state with the server's
      // resolved (clamped) value. The number input
      // then shows what was stored, not what the
      // admin typed pre-clamp.
      if (typeof data.config.live_feed_timeout_minutes === 'number') {
        setLiveFeedTimeoutMin(data.config.live_feed_timeout_minutes);
      }
      if (data.config.live_feed_item_color) {
        setItemColor(data.config.live_feed_item_color);
      }
      qc.invalidateQueries({ queryKey: ['public-board-config'] });
    },
    onError: (e) => { setErr(e instanceof ApiError ? e.message : 'Save failed.'); setOk(null); },
  });

  const togglePanel = (id: string) => {
    setEnabled((cur) => cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id]);
  };
  const movePanel = (id: string, dir: -1 | 1) => {
    setEnabled((cur) => {
      const i = cur.indexOf(id); const j = i + dir;
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur]; [next[i], next[j]] = [next[j]!, next[i]!]; return next;
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Public board</h1>
          <p className="text-ink-muted">
            The wall-monitor view at <a className="underline" href="/public-board" target="_blank" rel="noreferrer">/public-board</a>.
            Choose which panels rotate, how fast, and whether the board shows real names (opt-in still
            applies per user; turning anonymise off is an override for a private monitor behind Tailscale).
          </p>
        </div>
        {((teams.data?.teams ?? []).length) > 0 ? (
          <div className="flex items-center gap-2">
            <label htmlFor="public-board-team" className="text-xs text-ink-muted">Team</label>
            <select
              id="public-board-team"
              className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              {(teams.data?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        ) : teams.isLoading ? (
          <p className="text-xs text-ink-dim">Loading teams…</p>
        ) : null}
      </header>

      {err ? <p className="text-sm text-accent-danger">{err}</p> : null}
      {ok ? <p className="text-sm text-accent-success">{ok}</p> : null}

      <div className="card space-y-4">
        <div>
          <label className="label">Enabled panels (in rotation order)</label>
          <ul className="mt-2 space-y-1">
            {ALL_PANELS.map((p) => {
              const on = enabled.includes(p.id);
              const order = enabled.indexOf(p.id);
              return (
                <li key={p.id} className="flex items-center gap-3 rounded-md bg-bg-elevated p-2">
                  <input type="checkbox" className="h-4 w-4" checked={on} onChange={() => togglePanel(p.id)} />
                  <span className="flex-1"><span className="font-medium">{p.label}</span> <span className="text-xs text-ink-dim">{p.desc}</span></span>
                  {on ? (
                    <span className="flex gap-1 text-xs">
                      <button className="btn" onClick={() => movePanel(p.id, -1)} disabled={order === 0}>↑</button>
                      <button className="btn" onClick={() => movePanel(p.id, +1)} disabled={order === enabled.length - 1}>↓</button>
                      <span className="px-2 py-1 text-ink-dim">#{order + 1}</span>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="label">Rotation (seconds)</label>
            <input className="input" type="number" min="3" max="30" value={rotation} onChange={(e) => setRotation(Number(e.target.value))} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={anonymise} onChange={(e) => setAnonymise(e.target.checked)} />
              Anonymise (show handles only)
            </label>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={sound} onChange={(e) => setSound(e.target.checked)} />
              Sound (off by default)
            </label>
          </div>
        </div>
        {/* M5.11: live_feed timeout. Per-team, in minutes.
            The number input is the canonical admin surface
            for the timeout; the api clamps to [1, 10080]
            on PATCH so a typo can't persist a pathological
            value. The helper text below the input gives
            the admin a sense of the trade-off: short
            (5–15 min) is hyper-live, long (12–24h = 720–1440
            min) is a "today" feed. The unit label is in the
            text so the admin never has to guess ("60 what?
            seconds, hours?"). Min 1, max 10080 = 1 week.
            Both bounds are enforced by the server too;
            the min/max on the input is a UI affordance
            rather than a security boundary. */}
        <div className="space-y-1">
          <label className="label" htmlFor="live-feed-timeout">
            Live feed timeout (minutes)
          </label>
          <input
            id="live-feed-timeout"
            className="input max-w-xs"
            type="number"
            min={1}
            max={10080}
            step={1}
            value={liveFeedTimeoutMin}
            onChange={(e) => setLiveFeedTimeoutMin(Number(e.target.value))}
          />
          <p className="text-xs text-ink-dim">
            Events older than this are excluded from the live feed panel. Short
            (5–15) is hyper-live; medium (60, the default) is "last hour"; long
            (720–1440) is "today". 1 minute to 1 week (10080).
          </p>
        </div>
        {/*
          v0.6.2: live feed row color picker. The
          <input type="color"> is a native browser
          control — it gives the admin a swatch
          palette, hex field, and an "eye dropper" in
          supported browsers. The serialised value is
          '#rrggbb' (always 6 hex digits, lowercased
          in Chrome; the api's resolver is
          case-insensitive so any case works). We
          convert to the [r, g, b] array on save
          (and back from the response) so the api's
          stored shape and the live feed's read
          shape line up.

          Below the picker is a tiny 3-row preview
          that renders the same rowBackground()
          helper the live feed panel uses. The
          preview shows the operator what a row
          at the top of the feed (light tint),
          a row in the middle (mid), and a row
          at the bottom (deep, saturated) will
          look like on the wall TV. The preview
          reads the current itemColor and applies
          the same gradient formula; if the
          formula ever changes, the preview
          changes too.
        */}
        <div className="space-y-2">
          <label className="label" htmlFor="live-feed-item-color">
            Live feed row color
          </label>
          <div className="flex items-center gap-3">
            <input
              id="live-feed-item-color"
              type="color"
              className="h-10 w-16 cursor-pointer rounded border border-border bg-bg-elevated"
              value={rgbToHex(itemColor)}
              onChange={(e) => {
                const next = hexToRgb(e.target.value);
                if (next) setItemColor(next);
              }}
            />
            <code className="text-xs text-ink-dim">
              rgb({itemColor[0]}, {itemColor[1]}, {itemColor[2]}) —{' '}
              {rgbToHex(itemColor)}
            </code>
          </div>
          <p className="text-xs text-ink-dim">
            Color for the live feed row backgrounds. The transparency is
            derived from how recent each event is (newer = brighter, older =
            fainter), so the panel reads as a fade from the full color at
            the top to a faint tint at the bottom.
          </p>
          <div className="rounded-md border border-border p-3">
            <p className="mb-2 text-xs text-ink-dim">Preview (6 bands, newest at top)</p>
            <div className="flex flex-col gap-1.5">
              {[
                { recency: 0.08, label: 'Newest (band 0)' },
                { recency: 0.25, label: 'Very fresh (band 1)' },
                { recency: 0.42, label: 'Fresh (band 2)' },
                { recency: 0.58, label: 'Mid (band 3)' },
                { recency: 0.75, label: 'Fading (band 4)' },
                { recency: 0.92, label: 'At timeout (band 5)' },
              ].map((row) => (
                <div
                  key={row.recency}
                  className="rounded px-3 py-1.5 text-sm"
                  style={{ backgroundColor: rowBackground(itemColor, row.recency) }}
                >
                  <span className="text-ink">{row.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending || enabled.length === 0}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/*
        M5.14: Chronicle theme editor. Same teamId-driven
        layout as the panel settings above; lives on the
        same /admin/public-board page so the operator
        doesn't have to navigate to a separate route to
        tune the per-team chronicle. The editor is its own
        card with its own Save button — saving the panels
        config above doesn't save the chronicle config.
      */}
      <div className="card space-y-4">
        <header>
          <h2 className="text-lg font-semibold">Chronicle theme</h2>
          <p className="text-xs text-ink-dim">
            The Chronicles (<a className="underline" href="/chronicles" target="_blank" rel="noreferrer">/chronicles</a>)
            is a per-team hourly fantasy prose summary of recent activity. The theme controls voice,
            setting, character translations, and the per-team Ollama overrides.
          </p>
        </header>
        {teamId ? (
          <ChronicleThemeEditor teamId={teamId} />
        ) : (
          <p className="text-sm text-ink-dim">Pick a team above to edit its chronicle theme.</p>
        )}
      </div>
    </div>
  );
}

// v0.6.2: bridge between the api's [r, g, b] array
// shape and the native <input type="color">'s
// '#rrggbb' hex string. Both are canonical for
// their respective consumers; the api stores the
// array, the <input type="color"> reads/writes
// the hex string. round-trip-safe.
function rgbToHex([r, g, b]: [number, number, number]): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return '#' + [clamp(r), clamp(g), clamp(b)]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('');
}

function hexToRgb(s: string): [number, number, number] | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(s.trim());
  if (!m) return null;
  const hex = m[1]!;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}